/**
 * @fileoverview Two-step destructive confirmation for deleting a job's
 * mailbox (MOR-954).
 *
 * Step 1 spells out exactly what is permanently removed; step 2 requires
 * typing the full inbox address before the final delete button enables (the
 * server re-checks the address as a backstop). On success the mailbox flips
 * to "deleting" reactively, so the Mail page swaps to its teardown card and
 * this dialog simply closes.
 */

import { useEffect, useState } from "react";
import { useMutation } from "convex/react";
import { Loader2, TriangleAlert } from "lucide-react";
import { toast } from "sonner";

import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/ui/composed/overlays/dialog";
import { Button } from "@/ui/primitives/button";
import { Input } from "@/ui/primitives/input";
import { Label } from "@/ui/primitives/label";
import { extractErrorMessage } from "@/utils/errors";

interface DeleteMailboxDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mailboxId: Id<"jobMailboxes">;
  emailAddress: string;
}

export function DeleteMailboxDialog({
  open,
  onOpenChange,
  mailboxId,
  emailAddress,
}: DeleteMailboxDialogProps) {
  const deleteMailbox = useMutation(api.mail.mutations.deleteMailbox);
  const [step, setStep] = useState<"warning" | "confirm">("warning");
  const [confirmText, setConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);

  // Every open starts back at the warning with a blank confirmation field.
  useEffect(() => {
    if (open) {
      setStep("warning");
      setConfirmText("");
    }
  }, [open]);

  const addressMatches =
    confirmText.trim().toLowerCase() === emailAddress.trim().toLowerCase();

  const handleDelete = async () => {
    if (deleting || !addressMatches) return;
    setDeleting(true);
    try {
      await deleteMailbox({ mailboxId, confirmEmailAddress: confirmText });
      toast.success("Inbox deletion started");
      onOpenChange(false);
    } catch (err) {
      toast.error(extractErrorMessage(err, "Failed to delete the inbox"));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => !deleting && onOpenChange(next)}
    >
      <DialogContent>
        {step === "warning" ? (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-destructive">
                <TriangleAlert className="size-5 shrink-0" />
                Delete this inbox?
              </DialogTitle>
              <DialogDescription>
                This permanently deletes the inbox and everything in it:
              </DialogDescription>
            </DialogHeader>
            <ul className="list-disc space-y-1.5 pl-5 text-sm text-muted-foreground">
              <li>
                The email address{" "}
                <span className="font-mono font-medium text-foreground">
                  {emailAddress}
                </span>{" "}
                — mail sent to it will no longer be received
              </li>
              <li>All email threads and messages</li>
              <li>All drafts awaiting approval</li>
              <li>
                The inbox copies of attachments (documents already filed into
                the Library are kept)
              </li>
            </ul>
            <p className="text-sm font-medium text-destructive">
              This cannot be undone.
            </p>
            <p className="text-sm text-muted-foreground">
              Afterwards you can set up a new inbox for this job with a
              different address.
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={() => setStep("confirm")}>
                Continue
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="text-destructive">
                Confirm inbox deletion
              </DialogTitle>
              <DialogDescription>
                Type the full inbox address to confirm you want to permanently
                delete it and all of its mail.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-1.5">
              <Label htmlFor="confirm-mailbox-address">
                Type{" "}
                <span className="font-mono font-medium text-foreground">
                  {emailAddress}
                </span>{" "}
                to continue
              </Label>
              <Input
                id="confirm-mailbox-address"
                autoFocus
                autoComplete="off"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder={emailAddress}
                disabled={deleting}
              />
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                disabled={deleting}
                onClick={() => setStep("warning")}
              >
                Back
              </Button>
              <Button
                variant="destructive"
                disabled={!addressMatches || deleting}
                onClick={() => void handleDelete()}
              >
                {deleting && <Loader2 className="mr-1.5 size-4 animate-spin" />}
                Permanently delete inbox
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
